import { Box, Center, Icon, Spacer, Tag, TagLabel, TagLeftIcon, Text, Flex } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import {
  CalendarIcon,
  CheckCircleFilledIcon,
  EyeIcon,
  BarChart02Icon,
  CheckCircleBrokenIcon,
  BookOpen01Icon,
  Pencil02Icon,
  DotPoints01Icon,
  Target04Icon,
  BriefCase02Icon,
  HelpCircleIcon,
} from '../../assets/icons'
import formatDate from '../../helpers/formatDate'
import getIconPath from '../../helpers/getFileIcon'
import { statusColors } from '../../themes/components/tag'
import { ContentType, ContentStatus } from '../../types/content/content'

const ContentCard = ({
  title,
  type,
  media,
  timestamp,
  views,
  description,
  status,
  onTagClick,
  onSelect,
  isSelected,
}: {
  title: string
  type: ContentType
  media: string
  timestamp: string
  status: ContentStatus
  views: number
  description: string

  onTagClick?: () => void
  onSelect?: () => void
  isSelected?: boolean
}) => {
  const { t } = useTranslation()
  const typeMapping = {
    skills: {
      bg: statusColors.error.bg,
      borderColor: statusColors.error.border,
      color: statusColors.error.color,
      icon: <BarChart02Icon />,
    },
    competence: {
      bg: statusColors.warning.bg,
      borderColor: statusColors.warning.border,
      color: statusColors.warning.color,
      icon: (
        <Icon fill="none">
          <CheckCircleBrokenIcon />
        </Icon>
      ),
    },
    learning: {
      bg: statusColors.error.bg,
      borderColor: statusColors.error.border,
      color: statusColors.error.color,
      icon: <BookOpen01Icon />,
    },
    training: {
      bg: statusColors.error.bg,
      borderColor: statusColors.error.border,
      color: statusColors.error.color,
      icon: <Pencil02Icon />,
    },
    task: {
      bg: statusColors.warning.bg,
      borderColor: statusColors.warning.border,
      color: statusColors.warning.color,
      icon: <DotPoints01Icon />,
    },
    goal: {
      bg: statusColors.success.bg,
      borderColor: statusColors.success.border,
      color: statusColors.success.color,
      icon: <Target04Icon />,
    },
    note: {
      bg: statusColors.warning.bg,
      borderColor: statusColors.warning.border,
      color: statusColors.warning.color,
      icon: <BookOpen01Icon />,
    },
    survey: {
      bg: statusColors.success.bg,
      borderColor: statusColors.success.border,
      color: statusColors.success.color,
      icon: <BriefCase02Icon />,
    },
    anonymous_survey: {
      bg: statusColors.success.bg,
      borderColor: statusColors.success.border,
      color: statusColors.success.color,
      icon: <HelpCircleIcon />,
    },
  }

  const typeInfo = typeMapping[type as keyof typeof typeMapping]

  const typeKey = type === 'training' ? 'todo.trainAndTest' : `content.types.${type}`
  const translatedType = t(typeKey)
  const typeLabel = translatedType !== typeKey ? translatedType : String(type)

  return (
    <Box
      pos="relative"
      h="258px"
      borderRadius="12px"
      borderColor={isSelected ? 'primary.100' : 'solid.100'}
      borderWidth="1px"
      boxShadow="0px 4px 6px -2px rgba(0, 0, 0, 0.03)"
      p="12px"
      cursor="pointer"
      onClick={onSelect}
    >
      {isSelected && (
        <Icon
          w="24px"
          h="24px"
          viewBox="0 0 24 24"
          color="primary.100"
          top="12px"
          right="12px"
          pos="absolute"
          zIndex="1"
        >
          <CheckCircleFilledIcon />
        </Icon>
      )}
      <Center
        bg="solid.50"
        h="140px"
        borderRadius="8px"
        position="relative"
        bgImage={media}
        bgPos="center"
        bgSize="cover"
        bgRepeat="no-repeat"
      >
        {!media && (
          <Box
            mr="20px"
            minW="70px"
            h="70px"
            bgImage={getIconPath(type)}
            bgSize="contain"
            bgRepeat="no-repeat"
            bgPosition="center"
          />
        )}
      </Center>

      <Spacer h="12px" />
      <Flex direction="column">
        <Text textStyle="h-s" mb="4px" color="solid.900">
          {title}
        </Text>
        <Text textStyle="b-xs" mt={0} color="solid.500" noOfLines={1}>
          {description}
        </Text>
      </Flex>

      <Flex direction="row" justify="space-between" align="center" mt="6px">
        <Flex direction="row" gap="4px" alignItems="center" mr={1}>
          <Icon boxSize="16px" viewBox="0 0 24 24" color="solid.500">
            <CalendarIcon />
          </Icon>
          <Text textStyle="l-xs" color="solid.500" isTruncated>
            {formatDate(timestamp)}
          </Text>
          <Text textStyle="l-xs" color="solid.500" mt={-1}>
            •
          </Text>
          <Icon boxSize="16px" viewBox="0 0 24 24" color="solid.500">
            <EyeIcon />
          </Icon>
          <Text textStyle="l-xs" color="solid.500" isTruncated>
            {(views || 12)} {t('content.labels.viewsSuffix')}
          </Text>
        </Flex>
        <Tag
          borderRadius="14px"
          h="20px"
          w={{ base: 'auto', md: 'fit-content' }}
          bg={typeInfo?.bg}
          color={typeInfo?.color}
          borderColor={typeInfo?.borderColor}
          borderWidth="1px"
          whiteSpace="nowrap"
        >
          <TagLeftIcon>{typeInfo?.icon}</TagLeftIcon>
          <TagLabel textStyle="l-xs" whiteSpace="normal">
            {typeLabel}
          </TagLabel>
        </Tag>
      </Flex>
    </Box>
  )
}

export default ContentCard
